/**
 * Cookie Consent Service
 *
 * Manages cookie consent state, localStorage persistence, and dynamic script loading.
 * GDPR-compliant with three categories: Essential, Functional, Analytics.
 */

import { db } from '../config/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

// Centralized policy version - bump this to trigger re-consent
export const CONSENT_POLICY_VERSION = '1.0.0';

// localStorage key
const CONSENT_STORAGE_KEY = 'quickmeet_cookie_consent';
const SESSION_ID_KEY = 'quickmeet_session_id';

// Google Analytics config
const GA_MEASUREMENT_ID = 'G-DFHPTJNBJ1';
const ADSENSE_CLIENT_ID = 'ca-pub-5068279696254934';

/**
 * Generate a UUID for guest session tracking
 */
const generateSessionId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Get or create session ID for guests
 */
export const getSessionId = () => {
  let sessionId = localStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = generateSessionId();
    localStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
};

/**
 * Get current consent state from localStorage
 * Returns null if no consent has been given or policy version changed
 */
export const getConsent = () => {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) return null;

    const consent = JSON.parse(stored);

    // Check policy version - if different, require re-consent
    if (consent.policyVersion !== CONSENT_POLICY_VERSION) {
      return null;
    }

    return consent;
  } catch (error) {
    console.error('Error reading consent:', error);
    return null;
  }
};

/**
 * Check if user has given consent for a specific category
 */
export const hasConsent = (category) => {
  const consent = getConsent();
  if (!consent) return false;

  // Essential is always true if consent exists
  if (category === 'essential') return true;

  return consent[category] === true;
};

/**
 * Save consent to localStorage
 */
export const saveConsent = (preferences, source = 'banner') => {
  const consent = {
    essential: true, // Always true
    functional: preferences.functional ?? false,
    analytics: preferences.analytics ?? false,
    timestamp: new Date().toISOString(),
    policyVersion: CONSENT_POLICY_VERSION,
    source
  };

  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
    return consent;
  } catch (error) {
    console.error('Error saving consent:', error);
    return null;
  }
};

/**
 * Log consent to Firestore for compliance (only for logged-in users)
 */
export const logConsentToFirestore = async (consent, userId = null, action = 'custom') => {
  try {
    const sessionId = getSessionId();

    await addDoc(collection(db, 'consentLogs'), {
      userId: userId || null,
      sessionId,
      essential: consent.essential,
      functional: consent.functional,
      analytics: consent.analytics,
      action, // 'accept_all' | 'reject_non_essential' | 'custom'
      policyVersion: consent.policyVersion,
      timestamp: serverTimestamp(),
      userAgent: navigator.userAgent,
      source: consent.source
    });
  } catch (error) {
    // Don't block on logging errors - consent should still work
    console.error('Error logging consent to Firestore:', error);
  }
};

/**
 * Clear consent (for testing/debugging)
 */
export const clearConsent = () => {
  localStorage.removeItem(CONSENT_STORAGE_KEY);
};

/**
 * Dynamically load a script
 */
export const loadScript = (src, id) => {
  return new Promise((resolve, reject) => {
    // Check if script already loaded
    if (document.getElementById(id)) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.id = id;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

/**
 * Load Google Analytics (only if analytics consent given)
 */
export const loadGoogleAnalytics = async () => {
  if (!hasConsent('analytics')) {
    return false;
  }

  try {
    await loadScript(
      `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`,
      'ga-script'
    );

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID);

    return true;
  } catch (error) {
    console.error('Error loading Google Analytics:', error);
    return false;
  }
};

/**
 * Load Google AdSense (only if analytics consent given)
 */
export const loadGoogleAdSense = async () => {
  if (!hasConsent('analytics')) {
    return false;
  }

  try {
    await loadScript(
      `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`,
      'adsense-script'
    );
    return true;
  } catch (error) {
    console.error('Error loading Google AdSense:', error);
    return false;
  }
};

/**
 * Load all analytics/advertising scripts based on consent
 */
export const loadConsentedScripts = async () => {
  if (hasConsent('analytics')) {
    await Promise.all([
      loadGoogleAnalytics(),
      loadGoogleAdSense()
    ]);
  }
};

/**
 * Remove loaded scripts (for when consent is revoked)
 * Note: This removes script tags but already-executed code may still be in memory
 */
export const removeAnalyticsScripts = () => {
  const gaScript = document.getElementById('ga-script');
  const adsenseScript = document.getElementById('adsense-script');

  if (gaScript) gaScript.remove();
  if (adsenseScript) adsenseScript.remove();

  // Clear gtag
  window.dataLayer = [];
  delete window.gtag;
};

/**
 * Create consent object for "Accept All"
 */
export const createAcceptAllConsent = () => ({
  essential: true,
  functional: true,
  analytics: true
});

/**
 * Create consent object for "Reject Non-Essential"
 */
export const createRejectNonEssentialConsent = () => ({
  essential: true,
  functional: false,
  analytics: false
});
